"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Beaker, TrendingUp, Shield, FileCheck, Headphones, Lightbulb, ClipboardCheck, Cog, Rocket, Target, Zap, DollarSign, Users, Award, Check } from "lucide-react";
export default function OurPrivateLabelServices() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: workflowRef,
    isVisible: workflowVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: benefitsRef,
    isVisible: benefitsVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const services = [{
    icon: Beaker,
    title: "Product Formulation/Design",
    description: "Ready-to-market core technologies | Custom formulation or engineering variations | Performance and stability evaluations"
  }, {
    icon: TrendingUp,
    title: "Private-Label Customization",
    description: "Feature and ingredient modifications | Design changes based on target market needs |  Brand-specific performance tuning"
  }, {
    icon: FileCheck,
    title: "Manufacturing & Supply Chain Execution",
    description: "Selection and management of manufacturing partners | Supply chain coordination and production planning | Manufacturing onboarding, ramp-up, and ongoing support"
  }, {
    icon: Shield,
    title: "Quality & Compliance Ownership",
    description: "Product specifications | Testing summaries | Safety and regulatory documentation"
  }, {
    icon: Headphones,
    title: "Inventory Planning & Order Management",
    description: "Define MOQ, lead times and stock levels | Order planning and delivery scheduling | Ongoing inventory maintenance to ensure product availability"
  }];
  const workflowSteps = [{
    icon: Lightbulb,
    step: "01",
    title: "Discovery & Assessment",
    description: "We start by understanding your brand vision, target market, and product requirements. Our team evaluates technical feasibility and identifies the best approach for your private-label needs."
  }, {
    icon: ClipboardCheck,
    step: "02",
    title: "Planning & Customization",
    description: "Together we define product specifications, customization options, and manufacturing requirements. We develop a comprehensive project roadmap with clear timelines and milestones."
  }, {
    icon: Cog,
    step: "03",
    title: "Execution & Production",
    description: "Our team manages the entire manufacturing process, from partner selection to quality control. We coordinate supply chains, oversee production runs, and ensure compliance with all regulations."
  }, {
    icon: Rocket,
    step: "04",
    title: "Launch & Support",
    description: "We handle inventory planning, order fulfillment, and ongoing product support. Our team remains available for adjustments, scaling, and continuous improvement as your brand grows."
  }];
  const benefits = [{
    icon: Target,
    title: "Market-Ready Expertise",
    description: "Access proven technologies and formulations that accelerate your time-to-market while reducing development risk and costs."
  }, {
    icon: Zap,
    title: "Rapid Innovation",
    description: "Leverage cutting-edge materials and advanced technologies to create differentiated products that stand out in competitive markets."
  }, {
    icon: DollarSign,
    title: "Cost Efficiency",
    description: "Benefit from our established manufacturing relationships and economies of scale to optimize production costs without compromising quality."
  }, {
    icon: Users,
    title: "Dedicated Partnership",
    description: "Work with experienced professionals who understand your brand vision and provide ongoing support throughout your product lifecycle."
  }, {
    icon: Award,
    title: "Quality Assurance",
    description: "Trust in comprehensive quality control, regulatory compliance, and rigorous testing protocols that protect your brand reputation."
  }, {
    icon: Check,
    title: "End-to-End Support",
    description: "From concept development to manufacturing scale-up, inventory management, and beyond—we handle the complexity so you can focus on growth."
  }];
  return <>
      <div ref={sectionRef} className="pt-0 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            
            
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, i) => {
            const Icon = service.icon;
            const delays = ["", "animate-fade-in-up-delay-100", "animate-fade-in-up-delay-200", ""];
            return <Card key={i} data-index={i} className={`card group hover:shadow-medium transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] ${isVisible ? `animate-fade-in-up ${delays[i % 3]}` : "opacity-0"}`}>
                  <CardHeader className="flex flex-col items-center text-center">
                    <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary-light transition-colors duration-[var(--duration-medium)] group-hover:bg-primary">
                      <Icon className="h-8 w-8 text-primary transition-colors duration-[var(--duration-medium)] group-hover:text-white" strokeWidth={2} />
                    </div>
                    <CardTitle className="text-xl font-semibold text-foreground">
                      {service.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted text-center text-sm leading-relaxed opacity-0 group-hover:opacity-100 transition-opacity duration-[var(--duration-medium)]">
                      {service.description}
                    </p>
                  </CardContent>
                </Card>;
          })}
          </div>
        </div>
      </div>

      {}
      <div ref={workflowRef} className="bg-muted-light py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className={`text-4xl md:text-5xl font-display font-semibold text-foreground mb-4 ${workflowVisible ? "animate-fade-in-up" : "opacity-0"}`}>
              How We Work
            </h2>
            <p className={`text-muted text-lg max-w-3xl mx-auto ${workflowVisible ? "animate-fade-in-up-delay-100" : "opacity-0"}`}>
              Our proven four-step process ensures seamless private-label product development from concept to market launch
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {workflowSteps.map((step, i) => {
            const Icon = step.icon;
            const delays = ["", "animate-fade-in-up-delay-100", "animate-fade-in-up-delay-200", ""];
            return <Card key={i} data-index={i} className={`card group hover:shadow-medium transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] ${workflowVisible ? `animate-fade-in-up ${delays[i % 3]}` : "opacity-0"}`}>
                  <CardHeader>
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0">
                        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary-light transition-colors duration-[var(--duration-medium)] group-hover:bg-secondary">
                          <Icon className="h-7 w-7 text-secondary transition-colors duration-[var(--duration-medium)] group-hover:text-foreground-secondary" strokeWidth={2} />
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-semibold text-accent mb-1">
                          STEP {step.step}
                        </div>
                        <CardTitle className="text-2xl font-semibold text-foreground">
                          {step.title}
                        </CardTitle>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted text-sm leading-relaxed opacity-0 group-hover:opacity-100 transition-opacity duration-[var(--duration-medium)]">
                      {step.description}
                    </p>
                  </CardContent>
                </Card>;
          })}
          </div>
        </div>
      </div>

      {}
      <div ref={benefitsRef} className="bg-background py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className={`text-4xl md:text-5xl font-display font-semibold text-foreground mb-4 ${benefitsVisible ? "animate-fade-in-up" : "opacity-0"}`}>
              Why Brands Choose Us
            </h2>
            <p className={`text-muted text-lg max-w-3xl mx-auto ${benefitsVisible ? "animate-fade-in-up-delay-100" : "opacity-0"}`}>
              Flex DPS combines technical expertise, proven processes, and dedicated partnership to help you bring innovative products to market faster and more efficiently
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, i) => {
            const Icon = benefit.icon;
            const delays = ["", "animate-fade-in-up-delay-100", "animate-fade-in-up-delay-200", ""];
            return <Card key={i} data-index={i} className={`card group hover:shadow-medium transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] ${benefitsVisible ? `animate-fade-in-up ${delays[i % 3]}` : "opacity-0"}`}>
                  <CardHeader className="flex flex-col items-center text-center">
                    <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-accent-light transition-colors duration-[var(--duration-medium)] group-hover:bg-accent">
                      <Icon className="h-8 w-8 text-accent transition-colors duration-[var(--duration-medium)] group-hover:text-white" strokeWidth={2} />
                    </div>
                    <CardTitle className="text-xl font-semibold text-foreground">
                      {benefit.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted text-center text-sm leading-relaxed opacity-0 group-hover:opacity-100 transition-opacity duration-[var(--duration-medium)]">
                      {benefit.description}
                    </p>
                  </CardContent>
                </Card>;
          })}
          </div>
        </div>
      </div>
    </>;
}